import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { NavigateNext, InfoOutlineRounded } from '@mui/icons-material'
import {
  Box,
  Breadcrumbs,
  CardContent,
  Link,
  Stack,
  Typography,
  useTheme,
  Card,
} from '@mui/material'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { ModuleConfig } from '../../shared/model'
import { ContextModel, Module } from '../../shared/model/models'
import { diversityDimensionMap, moduleConfigMap } from '../../shared/constants'
import { setContextVariantGapLatest } from '../../store/actions'
import { useLoading } from '../../shared/hooks'
import ACTIONS from '../../store/constants'
import FsAlert, { FsAlertRef } from '../../components/common/atoms/Alert'
import { translatedDomainName } from '../../shared/utils'
import { getIcon } from '../../components/common/Icons'
import FsFullPageLoader from '../../components/common/atoms/FullPageLoader'
import FsGapHistory from '../../components/fs-gap-history/fs-gap-history'
import FsLeftInfoDrawer from '../../components/common/atoms/LeftInfoDrawer'

type NavigationParams = {
  id?: string
}

const FsTimeline = () => {
  const { t, i18n } = useTranslation()
  const { id } = useParams<NavigationParams>()
  const contextModelVariantId = parseInt(id!, 10)
  const [moduleConfig, setModuleConfig] = useState<ModuleConfig>()
  const [contextModels, setContextModels] = useState<ContextModel[]>([])
  const { modules, currentRun, contextVariantGapLatest } = useAppSelector(
    (state) => state.currentAnalysis,
  )
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const navigate = useNavigate()
  const [helpOpen, setHelpOpen] = useState(false)
  const handleHelpOpen = () => setHelpOpen(true)
  const handleHelpClose = () => setHelpOpen(false)

  const alertRef = useRef<FsAlertRef>(null)
  const cmvId = parseInt(id!, 10)

  useEffect(() => {
    if (currentRun) {
      if (!contextVariantGapLatest || isEmpty(contextVariantGapLatest[cmvId])) {
        dispatch(setContextVariantGapLatest({ contextVariantId: cmvId, date: currentRun.runAt }))
      }
    }
  }, [id, contextVariantGapLatest, currentRun])

  const findCurrentModule = (odules: Module[]) =>
    odules.find((module) =>
      module.contextModels.some((cm) =>
        cm.contextModelVariants.some((cmv) => cmv.id === contextModelVariantId),
      ),
    )

  const findCurrentContextModel = () =>
    contextModels?.find((cm) =>
      cm.contextModelVariants.some((cmv) => cmv.id === contextModelVariantId),
    )

  useEffect(() => {
    if (modules) {
      const currentModule = findCurrentModule(modules)
      if (currentModule) {
        setModuleConfig(moduleConfigMap.get(currentModule.name))
        setContextModels(currentModule.contextModels)
      }
    }
  }, [id, modules])

  const curDivDimName = findCurrentContextModel()?.diversityDimension.name ?? ''
  const diversityDimenstionConfig = diversityDimensionMap.get(curDivDimName)

  const { isFetching } = useLoading([ACTIONS.ANALYSIS.SET_CONTEXT_VARIANT_GAP_LATEST])

  const goiLabel =
    contextVariantGapLatest && contextVariantGapLatest[contextModelVariantId]
      ? translatedDomainName(
          contextVariantGapLatest[contextModelVariantId].description.groupOfInterest.groupVariable
            .label,
          t,
        )
      : ''
  const rgLabel =
    contextVariantGapLatest && contextVariantGapLatest[contextModelVariantId]
      ? translatedDomainName(
          contextVariantGapLatest[contextModelVariantId].description.referenceGroup.groupVariable
            .label,
          t,
        )
      : ''

  const latestGap = contextVariantGapLatest ? contextVariantGapLatest[cmvId] : null

  const helpTextHtml =
    latestGap && latestGap.gapResultDescriptions.filter((desc) => desc.locale === i18n.language)
      ? latestGap.gapResultDescriptions.filter((desc) => desc.locale === i18n.language)[0]
          .gapMonitoring
      : ''

  return (
    <div>
      <Stack
        mb={2}
        direction={{ sm: 'column', md: 'row' }}
        alignItems={{ sm: 'flex-start', md: 'center' }}
        justifyContent={{ sm: 'center', md: 'space-between' }}
      >
        <Box>
          <Stack direction="row" alignItems="center">
            <Box mr={1} mt={0.75}>
              {getIcon(moduleConfig?.module ?? '', {
                color: moduleConfig?.fsColor[theme.palette.mode === 'light' ? 'primary' : 'light'],
              })}
            </Box>
            {getIcon(diversityDimenstionConfig?.diversityDimension ?? '', {
              color:
                diversityDimenstionConfig?.fsColor[
                  theme.palette.mode === 'light' ? 'primary' : 'light'
                ],
            })}
            <Typography variant="h1" ml={1} component="div" className="flex items-end">
              {t(`module.name.${moduleConfig?.module}` as any)} {t('by')}{' '}
              {t(`context.model.name.${curDivDimName}` as any)}
              <Typography variant='h3' ml={1}>
                ({goiLabel} {t('vs.')} {rgLabel})
              </Typography>
            </Typography>
            <Box
              pl={1}
              pt={1}
              onClick={handleHelpOpen}
              sx={{ cursor: 'pointer', color: theme.palette.text.secondary }}
            >
              <InfoOutlineRounded sx={{ fontSize: 26 }} />
            </Box>
          </Stack>
          <Stack direction="row" alignItems="center" mt={1}>
            <Breadcrumbs separator={<NavigateNext fontSize="small" />} aria-label="breadcrumb">
              <Link
                underline="hover"
                key="1"
                color="text.secondary"
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate('/fs/dashboard')}
              >
                <Typography variant='body2'>{t('overview')}</Typography>
              </Link>
              <Link
                underline="hover"
                key="2"
                color="text.secondary"
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  navigate(`/fs/fair-${moduleConfig?.module}/${id}`)
                }}
              >
                <Typography variant='body2'>
                  {t(`module.name.${moduleConfig?.module}` as any)} {t('by')}{' '}
                  {t(`context.model.name.${curDivDimName}` as any)}
                </Typography>
              </Link>
              <Typography variant='body2' color="text.primary" className='font-semibold'>
                {t('gap_history')}
              </Typography>
            </Breadcrumbs>
          </Stack>
        </Box>
      </Stack>

      {latestGap && (
        <Box sx={{ overflowY: 'auto', height: 'calc(100vh - 183px)' }} p={1}>
          <Card
            elevation={1}
            sx={{ boxShadow: `2px 2px 8px 2px ${moduleConfig?.fsColor.primary}66` }}
            className="w-full"
          >
            <CardContent>
              <FsGapHistory
                gap={latestGap}
                contextModelVariantId={cmvId}
                moduleConfig={moduleConfig}
                diversityDimenstionConfig={diversityDimenstionConfig}
              />
            </CardContent>
          </Card>
        </Box>
      )}

      <FsLeftInfoDrawer open={helpOpen} onClose={handleHelpClose}>
        <Typography component="div" style={{ whiteSpace: 'pre-line' }}>
          {helpTextHtml}
        </Typography>
      </FsLeftInfoDrawer>

      {isFetching && <FsFullPageLoader />}
      <FsAlert ref={alertRef} />
    </div>
  )
}

export default FsTimeline
